// Client-side mirror of backend/buyer/services/metrics.py. The detail view
// derives its headline numbers (signals exposed, visibility, SoV) straight from
// the latest DONE run's result envelope it already fetches - rather than relying
// on the buyer's denormalized metric columns.
//
// Why: the Jarts finalize webhook flips a run to DONE WITHOUT computing those
// columns (they're filled lazily on the first detail GET / by the sweeper cron).
// So for a just-completed run, `buyer.metrics` can be null even though the run
// has full judgment data - which would show a false "No AI Trust Signals judged"
// donut. Deriving from the result makes the donut/visibility/SoV correct
// immediately and consistent with the Big-5 card (which also reads the result).
//
// Keep in lockstep with metrics.py (focus_visibility / focus_share_of_voice /
// count_focus_signals_exposed). Each value is null when the envelope can't
// support it (no result, nothing judged) so the UI renders an honest "-".

const TRUST_SIGNALS_JUDGED = 18; // leaf signals the judge evaluates (display is /19)

type Signal = { key?: unknown; exposed?: boolean };
type Subject = { isFocus?: boolean; signals?: Signal[] };
type Judgment = { subjects?: Subject[] } | null | undefined;
type ResultQuestion = { questionId?: string; judgment?: Judgment };
type ResultRun = { questions?: ResultQuestion[] };
type AnalysisResult = { runs?: ResultRun[] };

/** Subjects of each judged answer (a "judged" answer has a judgment dict whose
 *  `subjects` is an array - mirrors metrics.py `_iter_judgments`). */
function* iterJudgedSubjects(result: unknown): Generator<Subject[]> {
  const runs = (result as AnalysisResult | null | undefined)?.runs ?? [];
  for (const run of runs) {
    for (const q of run?.questions ?? []) {
      const subjects = q?.judgment?.subjects;
      if (Array.isArray(subjects)) {
        yield subjects.filter((s): s is Subject => !!s && typeof s === "object");
      }
    }
  }
}

export interface DerivedMetrics {
  signalsExposed: number | null;
  visibility: number | null;
  sov: number | null;
}

/** Mirror of metrics.py `compute_analysis_metrics`, over the same envelope. */
export function deriveMetricsFromResult(result: unknown): DerivedMetrics {
  let judged = 0;
  let focusAnswers = 0; // answers with an isFocus subject (visibility numerator)
  let focusAppearances = 0; // focus counted once per answer (SoV numerator)
  let totalAppearances = 0; // every enumerated subject (SoV denominator)
  const exposedKeys = new Set<unknown>();

  for (const subjects of iterJudgedSubjects(result)) {
    judged += 1;
    totalAppearances += subjects.length;
    const hasFocus = subjects.some((s) => s.isFocus);
    if (hasFocus) {
      focusAnswers += 1;
      focusAppearances += 1; // cap at once per answer (matches metrics.py)
    }
    for (const s of subjects) {
      if (!s.isFocus) continue;
      for (const sig of s.signals ?? []) {
        if (sig && sig.exposed && sig.key != null) exposedKeys.add(sig.key);
      }
    }
  }

  return {
    signalsExposed: judged === 0 ? null : Math.min(exposedKeys.size, TRUST_SIGNALS_JUDGED),
    visibility: judged === 0 ? null : focusAnswers / judged,
    sov: totalAppearances === 0 ? null : focusAppearances / totalAppearances,
  };
}
